import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, CreditCard, Shield, Clock, TrendingUp, Users, BarChart3, Bell, Package, Calendar, ArrowRight, MessageCircle, CheckCircle, Target, Award, Monitor, Gift, Briefcase, ShoppingCart, Music2, Building2, Trophy, AlertCircle, Activity, Lightbulb, Heart, Smartphone, Camera } from 'lucide-react';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp, stagger } from '@/lib/motion';

export default function ArriendoLeasingPage() {
  const planes = [
    {
      name: 'Básico',
      subtitle: 'Perfecto para eventos pequeños',
      price: 'Desde $290.000',
      period: '/evento',
      features: ['1-2 kioscos Venddi™', 'Pin pad integrado', 'Setup + traslado incluido', 'Técnico on-call', 'Dashboard básico', 'Soporte 12/5'],
      ideal: 'Cumpleaños, fiestas privadas, eventos corporativos pequeños',
      color: '#0EA5E9'
    },
    {
      name: 'Avanzado',
      subtitle: 'Para discotecas y eventos medianos',
      price: 'Desde $590.000',
      period: '/evento',
      featured: true,
      features: ['3-6 kioscos Venddi™', 'Pin pad + NFC contactless', 'Setup + traslado sin costo', 'Técnico permanente en evento', 'Plataforma Venddi™ completa', 'Dashboard tiempo real', 'Alertas stock automáticas', 'Pre-compra de tragos', 'Soporte 24/7'],
      ideal: 'Discotecas, fiestas masivas, food trucks, ferias gastronómicas',
      color: '#0191B4'
    },
    {
      name: 'Pro Masivos',
      subtitle: 'Festivales y eventos de miles',
      price: 'Cotización',
      period: 'personalizada',
      features: ['10+ kioscos Venddi™', 'Pin pad + NFC + QR', 'Logística completa', '3+ técnicos permanentes', 'Plataforma enterprise', 'Dashboard multi-punto', 'IA predicción demanda', 'Fidelización + pre-compra', 'Centralización food trucks', 'Reportes en vivo', 'Soporte 24/7 dedicado'],
      ideal: 'Lollapalooza, Primavera Fauna, festivales +5,000 personas',
      color: '#3B82F6'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#0E3A5F] to-[#0A2540] text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#0EA5E9] blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#0191B4] blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0EA5E9]/20 border border-[#0EA5E9]/30 mb-6">
                <Zap className="w-4 h-4 text-[#0EA5E9]" strokeWidth={2} />
                <span className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider">Powered by ALLCOM</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-bold tracking-tight leading-tight mb-6" style={{fontFamily:'Syne,sans-serif'}}>
                <span className="text-[#0EA5E9]">Venddi<span className="text-3xl align-super">™</span></span><br/>
                Kioscos para eventos<br/>que venden solos
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                Arriendo de kioscos inteligentes para fiestas, discotecas, matrimonios y eventos masivos. <span className="text-white font-semibold">Instalación express, técnico permanente y dashboard en vivo.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="#planes" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-[#0EA5E9] to-[#0191B4] rounded-xl hover:shadow-2xl transition-all">
                  <Calendar className="w-5 h-5" /> Ver planes
                </a>
                <a href="https://wa.me/56975294340?text=Necesito%20kioscos%20Venddi" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-emerald-400 border-2 border-emerald-400/30 rounded-xl hover:bg-emerald-400/10 transition-all">
                  <MessageCircle className="w-5 h-5" /> Cotizar evento
                </a>
              </div>
              
              <div className="flex flex-wrap gap-6">
                {['Traslado sin costo RM, VI, V', 'Técnico 12/7', 'Dashboard tiempo real', 'Setup 3 horas'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-[#0EA5E9]/30">
                <img src="/venddi-kioscos-autoservicio-eventos-fiestas-chile.webp" alt="Venddi kioscos autoservicio eventos fiestas Chile" className="w-full h-auto" />
              </div>
              <div className="absolute -bottom-6 -right-6 px-6 py-3 bg-[#0EA5E9] rounded-xl shadow-xl">
                <div className="text-2xl font-bold text-white" style={{fontFamily:'Syne,sans-serif'}}>+15K</div>
                <div className="text-xs text-sky-100">Transacciones/evento</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 10 KPIs */}
      <Section>
        <SectionTitle badge="POR QUÉ Venddi™" title="10 razones para elegirnos" subtitle="Tecnología + soporte + inteligencia." center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {[
            { metric: '3x', label: 'Más rápido que efectivo', icon: Zap },
            { metric: '12/7', label: 'Técnico permanente', icon: Shield },
            { metric: 'RM, VI, V', label: 'Traslado incluido', icon: Gift },
            { metric: '30 seg', label: 'Transacción promedio', icon: Clock },
            { metric: 'Tiempo real', label: 'Dashboard ventas', icon: BarChart3 },
            { metric: 'Automáticas', label: 'Alertas de stock', icon: Bell },
            { metric: 'Integrado', label: 'Pin pad + NFC + QR', icon: CreditCard },
            { metric: 'Propio', label: 'Software Venddi™', icon: Monitor },
            { metric: 'Ilimitado', label: 'Pre-compra tragos', icon: Package },
            { metric: '+98%', label: 'Uptime garantizado', icon: Target },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <motion.div key={i} variants={fadeUp} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center hover:shadow-lg transition-all">
                <Icon className="w-8 h-8 mx-auto mb-3 text-[#0191B4]" strokeWidth={1.5} />
                <div className="text-xl font-bold mb-1 text-[#0191B4]" style={{fontFamily:'Syne,sans-serif'}}>{kpi.metric}</div>
                <div className="text-xs text-slate-600">{kpi.label}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* Casos de Uso */}
      <Section alt>
        <SectionTitle badge="CASOS DE USO" title="Venddi™ en cualquier evento" subtitle="Desde fiestas privadas hasta estadios." center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {[
            { icon: Users, title: 'Fiestas & Discotecas', desc: 'Tragos, cover, guardarropía.', example: 'Cover · Tragos · Guardarropía' },
            { icon: Briefcase, title: 'Eventos Corporativos', desc: 'Aniversarios, team building.', example: 'Team building · Aniversarios · Lanzamientos' },
            { icon: ShoppingCart, title: 'Ferias & Food Trucks', desc: 'Centralización ventas.', example: 'Ferias gastro · Food trucks · Mercados' },
            { icon: Music2, title: 'Festivales Masivos', desc: '10+ kioscos sincronizados.', example: 'Conciertos · Festivales · Eventos masivos' },
            { icon: Building2, title: 'Hoteles & Matrimonios', desc: 'Eventos premium, bodas, matrimonios.', example: 'Bodas · Matrimonios · Cocteles de boda' },
            { icon: Trophy, title: 'Estadios', desc: 'Partidos, marathons.', example: 'Fútbol · Conciertos · Marathons' },
            { icon: Users, title: 'Universidades', desc: 'Fiestas mechonas.', example: 'Mechonas · Aniversarios · Eventos estudiantiles' },
          ].map((useCase, i) => {
            const Icon = useCase.icon;
            return (
              <motion.div key={i} variants={fadeUp} className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm text-center hover:shadow-lg transition-all">
                <Icon className="w-10 h-10 mx-auto mb-3 text-[#0191B4]" strokeWidth={1.5} />
                <h4 className="text-base font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{useCase.title}</h4>
                <p className="text-xs text-slate-600 mb-2">{useCase.desc}</p>
                <p className="text-xs text-slate-400"><span className="text-[#0EA5E9] font-semibold">Ej:</span> {useCase.example}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* 3 Planes */}
      <Section>
        <SectionTitle badge="PLANES" title="Elige tu plan" subtitle="Desde fiestas hasta festivales." center />
        <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto" id="planes">
          {planes.map((plan, i) => (
            <motion.div key={i} variants={fadeUp} className={`relative p-8 rounded-2xl border-2 shadow-lg ${plan.featured ? 'border-[#0191B4] scale-105' : 'border-slate-200'} bg-white`}>
              {plan.featured && <div className="absolute -top-4 left-1/2 -translate-x-1/2"><span className="px-4 py-1.5 bg-[#0191B4] text-white text-xs font-bold uppercase rounded-full shadow-lg">Más Elegido</span></div>}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-1" style={{fontFamily:'Syne,sans-serif', color: plan.color}}>{plan.name}</h3>
                <p className="text-sm text-slate-600 mb-4">{plan.subtitle}</p>
                <div className="text-3xl font-bold mb-1" style={{fontFamily:'Syne,sans-serif', color: plan.color}}>{plan.price}</div>
                <div className="text-xs text-slate-500">{plan.period}</div>
              </div>
              <div className="space-y-3 mb-6">
                {plan.features.map((feature, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" style={{color: plan.color}} strokeWidth={2} />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl bg-slate-50 mb-6">
                <p className="text-xs text-slate-600"><span className="font-semibold">Ideal:</span> {plan.ideal}</p>
              </div>
              <a href={`https://wa.me/56975294340?text=Cotizar%20plan%20${plan.name}`} className="flex items-center justify-center gap-2 w-full px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all" style={{backgroundColor: plan.color}}>
                Cotizar {plan.name} <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Dashboard 3 Vistas */}
      <Section alt>
        <SectionTitle 
          badge="DASHBOARD VENDDI™" 
          title="3 vistas del sistema" 
          subtitle="Organizador, vendedor y locatario. Cada uno ve lo que necesita en tiempo real."
          center 
        />
        
        <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {[
            {
              role: 'Organizador del Evento',
              icon: Users,
              screenshot: '/ceo_cockpit_dashboard.png',
              desc: 'Vista completa de todos los puntos de venta',
              features: ['Ventas totales en vivo', 'Ranking por stand/food truck', 'Top 10 productos del evento', 'Alertas de stock por vendedor', 'Comparativas por hora/día', 'Proyección de cierre']
            },
            {
              role: 'Food Truck / Vendedor',
              icon: ShoppingCart,
              screenshot: '/analytics_dashboard.png',
              desc: 'Dashboard personalizado con sus ventas',
              features: ['Solo sus ventas y productos', 'Gráfico ventas por hora', 'Productos más vendidos', 'Alertas cuando se acaba stock', 'Saldo acumulado en vivo', 'Descargar su reporte']
            },
            {
              role: 'Locatario (Estadio/Colegio)',
              icon: Building2,
              screenshot: '/control_panel_dashboard.webp',
              desc: 'Vista operativa del recinto',
              features: ['Estado de todos los kioscos', 'Mapa del recinto con ventas', 'Afluencia por zona', 'Ingresos totales del local', 'Comisión por arriendo espacio', 'Reporte para contabilidad']
            }
          ].map((view, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="rounded-2xl bg-white border-2 border-slate-200 shadow-lg overflow-hidden hover:shadow-2xl hover:border-[#0191B4]/30 transition-all"
            >
              {/* Screenshot */}
              <div className="relative h-48 bg-slate-900 overflow-hidden">
                <img 
                  src={view.screenshot} 
                  alt={`Dashboard ${view.role}`} 
                  className="w-full h-full object-cover opacity-90"
                />
                <div className="absolute top-3 left-3 px-3 py-1 bg-emerald-500 text-white text-xs font-bold uppercase rounded-full">
                  En vivo
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <view.icon className="w-8 h-8 text-[#0191B4]" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-base font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                      {view.role}
                    </h4>
                    <p className="text-xs text-slate-500">{view.desc}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {view.features.map((feature, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#0191B4] shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-xs text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500">
            Cada usuario accede con su login único. <span className="font-semibold text-[#0191B4]">Datos en tiempo real</span> desde cualquier dispositivo.
          </p>
        </div>
      </Section>

      {/* Kian™ - Asistente IA */}
      <Section alt>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 mb-4">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">IA en Vivo</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{fontFamily:'Syne,sans-serif'}}>
              Kian™ - Tu CAIO en cada evento
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
              No solo tienes kioscos. Tienes un Chief AI Officer que dirige tus ventas en tiempo real, optimiza precios y te avisa de todo por WhatsApp.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start mb-10">
            {/* Left: Kian */}
            <div className="flex flex-col items-center text-center">
              <img src="/kian_caio.webp" alt="Kian CAIO" className="w-48 h-48 rounded-full object-cover shadow-2xl ring-4 ring-[#0EA5E9]/30 mb-6" />
              <h3 className="text-2xl font-bold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>
                Kian<span className="text-lg align-super">™</span>
              </h3>
              <p className="text-sm font-semibold text-[#0EA5E9] mb-4">Chief AI Officer</p>
              <p className="text-sm text-slate-600 leading-relaxed max-w-sm">
                Asistente de IA que analiza cada venta, detecta patrones y te manda acciones en tiempo real para maximizar ingresos.
              </p>
              
              <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <MessageCircle className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                <span className="text-xs font-semibold text-emerald-700">Notificaciones por WhatsApp</span>
              </div>
            </div>

            {/* Right: Qué hace */}
            <div className="space-y-4">
              {[
                { icon: AlertCircle, title: 'Detecta agotamiento', msg: 'Hot dogs se agotan en 15 min', action: 'Alerta para restock', color: '#0191B4', bg: '#0191B4' },
                { icon: TrendingUp, title: 'Optimiza precios', msg: 'Tragos +60% vs hora anterior', action: 'Sugiere subir precio 10%', color: '#10B981', bg: '#10B981' },
                { icon: Target, title: 'Activa promos', msg: 'Papas mal vendidas', action: 'Activa 2x1 automático', color: '#0191B4', bg: '#0191B4' },
                { icon: Activity, title: 'Predice picos', msg: 'Pico demanda en 30 minutos', action: 'Prepara más stock', color: '#10B981', bg: '#10B981' },
                { icon: Lightbulb, title: 'Sugerencias IA', msg: 'Baja precio bebidas sin alcohol', action: 'Para aumentar volumen', color: '#0191B4', bg: '#0191B4' },
              ].map((notif, i) => {
                const Icon = notif.icon;
                return (
                  <div key={i} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{backgroundColor: `${notif.bg}15`}}>
                        <Icon className="w-6 h-6" style={{color: notif.color}} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h5 className="text-sm font-semibold text-[#0A2540] mb-1">{notif.title}</h5>
                        <p className="text-xs text-slate-600 mb-1">"{notif.msg}"</p>
                        <p className="text-xs font-medium" style={{color: notif.color}}>→ {notif.action}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeline ejemplo */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-[#0A2540] border border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-white">Notificaciones Kian™ por WhatsApp</span>
            </div>
            <div className="space-y-3">
              {[
                { time: '21:15', msg: '🔥 Hot dogs se agotan en 15 min - Restock urgente' },
                { time: '21:42', msg: '📈 Tragos +60% vs hora anterior - Considera subir precio' },
                { time: '22:10', msg: '🎯 Papas mal vendidas - Activé promoción 2x1' },
                { time: '22:35', msg: '⚡ Pico demanda detectado en 30 min - Prepara stock' },
              ].map((notif, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="text-slate-500 font-mono text-xs">{notif.time}</span>
                  <span className="text-slate-200">{notif.msg}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Gamificación */}
      <Section alt>
        <SectionTitle badge="GAMIFICACIÓN" title="Módulos que nadie más tiene" subtitle="Engagement y ventas." center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {[
            {title: 'Ruleta Casino', desc: 'Premios sorpresa'},
            {title: 'Tragamonedas', desc: 'Jackpot descuentos'},
            {title: 'Crush Mode', desc: 'Invita un trago'},
            {title: 'Pre-compra', desc: 'Antes del evento'},
            {title: 'Gana Trago', desc: 'Datos = trago'},
            {title: 'Shot Roulette', desc: 'Competencia mesas'},
            {title: 'Playlist Vote', desc: 'VIP eligen música'},
            {title: 'IG Check-in', desc: 'Comparte = 10% off'},
            {title: 'Happy Hour IA', desc: 'Precios dinámicos'},
            {title: 'Entradas', desc: 'Impresión auto'},
          ].map((mod, i) => (
            <motion.div key={i} variants={fadeUp} className="p-5 rounded-xl bg-white border border-slate-200 text-center">
              <Award className="w-8 h-8 mx-auto mb-3 text-[#0191B4]" strokeWidth={1.5} />
              <h5 className="text-sm font-semibold text-[#0A2540] mb-1">{mod.title}</h5>
              <p className="text-[10px] text-slate-500">{mod.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Crush Connect™ */}
      <Section>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 border border-pink-200 mb-4">
              <Heart className="w-4 h-4 text-pink-500" strokeWidth={2} />
              <span className="text-xs font-semibold text-pink-600 uppercase tracking-wider">Solo en Venddi™</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{fontFamily:'Syne,sans-serif'}}>
              Crush Connect<span className="text-xl align-super">™</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
              El módulo más viral. Conoce gente mientras compras. Perfecto para fiestas, discotecas y eventos sociales.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-10">
            {/* Left: Cómo funciona */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                Cómo funciona
              </h3>
              {[
                { step: '1', title: 'Escanea QR', desc: 'Escaneas QR del kiosco y te registras por WhatsApp', icon: Smartphone },
                { step: '2', title: 'Manda tu selfie', desc: 'Envías selfie + nombre por WhatsApp. Kian te pregunta si quieres aparecer', icon: Camera },
                { step: '3', title: 'Apareces en galería', desc: 'Tu foto sale en pantalla del kiosco con tu nombre', icon: Users },
                { step: '4', title: 'Alguien te contacta', desc: 'Personas interesadas escanean tu QR y te escriben por WhatsApp', icon: MessageCircle },
              ].map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white text-sm font-bold shrink-0" style={{fontFamily:'Syne,sans-serif'}}>
                      {step.step}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-5 h-5 text-[#0191B4]" strokeWidth={1.5} />
                        <h5 className="text-sm font-semibold text-[#0A2540]">{step.title}</h5>
                      </div>
                      <p className="text-xs text-slate-600">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: Beneficios */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-pink-50 to-white border border-pink-200">
              <h4 className="text-lg font-semibold text-[#0A2540] mb-6" style={{fontFamily:'Syne,sans-serif'}}>
                Por qué funciona
              </h4>
              <div className="space-y-4">
                {[
                  { icon: TrendingUp, text: '+30% engagement en kiosco', color: '#10B981' },
                  { icon: Users, text: 'Más socialización = más tiempo en evento', color: '#0191B4' },
                  { icon: Zap, text: 'Viral: Gente comparte en redes', color: '#10B981' },
                  { icon: Shield, text: 'Opt-in voluntario + moderación IA', color: '#0191B4' },
                  { icon: Gift, text: 'Se borra al final (privacidad)', color: '#10B981' },
                ].map((benefit, i) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <Icon className="w-5 h-5 shrink-0" style={{color: benefit.color}} strokeWidth={2} />
                      <span className="text-sm text-slate-700">{benefit.text}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 rounded-xl bg-white border border-pink-200">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-pink-600">Perfecto para:</span> Fiestas universitarias, discotecas, eventos +21, matrimonios (solteros invitados), after office.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a href="https://wa.me/56975294340?text=Quiero%20Crush%20Connect%20en%20mi%20evento" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:shadow-2xl transition-all font-semibold">
              <Heart className="w-5 h-5" /> Activar Crush Connect™ en mi evento
            </a>
          </div>
        </div>
      </Section>

      {/* Cómo recibes tu dinero */}
      <Section alt>
        <SectionTitle 
          badge="TRANSPARENCIA TOTAL" 
          title="Cómo recibes tu dinero" 
          subtitle="Ves cada venta en vivo. Transferencia automática. Cuadratura perfecta. Simple y seguro."
          center 
        />
        
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline line - solo desktop */}
            <div className="absolute left-6 sm:left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#0EA5E9] to-[#0191B4] hidden md:block" />
            
            <div className="space-y-4 sm:space-y-6">
              {[
                { step: '1', title: 'Durante el evento', desc: 'Ves cada transacción EN VIVO en tu dashboard. Saldo actualizado al instante.', icon: BarChart3, badge: 'En vivo' },
                { step: '2', title: 'Cierre automático', desc: 'Sistema genera PDF completo: total recaudado, # transacciones, comisión, monto a transferir.', icon: CheckCircle, badge: 'Al cerrar' },
                { step: '3', title: 'Transferencia express', desc: '80% en tu cuenta en 24 horas. 20% después de cuadratura bancaria (48h).', icon: Zap, badge: '24-48 horas' },
                { step: '4', title: 'Comprobante + Cuadratura', desc: 'WhatsApp con comprobante bancario + PDF con cada transacción para tu contabilidad.', icon: Shield, badge: 'Garantizado' },
              ].map((step, i) => (
                <motion.div key={i} variants={fadeUp} className="relative flex gap-3 sm:gap-6 items-start">
                  <div className="relative z-10 w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#0191B4] flex items-center justify-center text-white text-xl sm:text-2xl font-bold shrink-0 shadow-lg" style={{fontFamily:'Syne,sans-serif'}}>
                    {step.step}
                  </div>
                  <div className="flex-1 pt-0 sm:pt-2">
                    <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white border border-slate-200 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0 mb-2 sm:mb-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <step.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0191B4]" strokeWidth={1.5} />
                          <h4 className="text-base sm:text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                            {step.title}
                          </h4>
                        </div>
                        <span className="text-xs font-semibold text-[#0EA5E9] bg-[#0EA5E9]/10 px-3 py-1 rounded-full self-start sm:whitespace-nowrap">
                          {step.badge}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Comisión Transparente */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#0A2540] mb-3" style={{fontFamily:'Syne,sans-serif'}}>
              Comisión clara y competitiva
            </h3>
            <p className="text-slate-600">
              No escondemos nada. Te mostramos exactamente cuánto cuesta y por qué es justo.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="p-6 rounded-2xl bg-white border-2 border-[#0191B4] shadow-lg text-center">
              <div className="text-3xl font-bold text-[#0191B4] mb-2" style={{fontFamily:'Syne,sans-serif'}}>2.9%</div>
              <div className="text-sm font-semibold text-[#0A2540] mb-1">Venddi™</div>
              <div className="text-xs text-slate-500">+ IVA por transacción</div>
            </div>
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center opacity-75">
              <div className="text-3xl font-bold text-slate-500 mb-2" style={{fontFamily:'Syne,sans-serif'}}>3.5%</div>
              <div className="text-sm font-semibold text-slate-600 mb-1">Transbank</div>
              <div className="text-xs text-slate-400">+ IVA estándar</div>
            </div>
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center opacity-75">
              <div className="text-3xl font-bold text-slate-500 mb-2" style={{fontFamily:'Syne,sans-serif'}}>3.9%</div>
              <div className="text-sm font-semibold text-slate-600 mb-1">Stripe/PayPal</div>
              <div className="text-xs text-slate-400">+ IVA internacional</div>
            </div>
          </div>
          
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-1" strokeWidth={2} />
              <div>
                <h5 className="text-sm font-semibold text-[#0A2540] mb-2">Garantía de pago ALLCOM</h5>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Respaldado por ALLCOM con <span className="font-semibold">20 años de experiencia</span> y <span className="font-semibold">469 proyectos ejecutados</span>. Si hay cualquier problema con tu pago, lo solucionamos inmediatamente o te compensamos con eventos futuros gratis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ Actualizado */}
      <Section alt>
        <SectionTitle badge="PREGUNTAS FRECUENTES" title="Dudas sobre Venddi™" center />
        <div className="max-w-3xl mx-auto space-y-4">
          {[
            { q: '¿Cuándo recibo mi dinero?', a: '80% del total recaudado se transfiere automáticamente a tu cuenta en 24 horas. El 20% restante después de la cuadratura bancaria (48 horas). Recibes comprobante por WhatsApp.' },
            { q: '¿Cuánto es la comisión?', a: '2.9% + IVA por cada transacción con tarjeta. Menor que Transbank (3.5%) y mucho menor que PayPal (3.9%). La comisión cubre: procesamiento bancario, plataforma, técnico y soporte 24/7.' },
            { q: '¿Cómo sé que todo cuadra?', a: 'Dashboard en vivo muestra cada transacción al instante. Al cerrar, recibes PDF completo con cada venta detallada. Puedes comparar con tu cierre de caja físico. Match perfecto garantizado.' },
            { q: '¿Qué pasa si hay un error en la cuadratura?', a: 'Lo solucionamos inmediatamente. Si hay diferencia, te transferimos el monto correcto en el día + compensación. 20 años de experiencia nos respaldan.' },
            { q: '¿A qué cuenta me transfieren?', a: 'A la cuenta bancaria que nos indiques antes del evento (empresa o personal). Transferencia directa, sin intermediarios.' },
            { q: '¿Puedo ver las transacciones en tiempo real?', a: 'Sí. Dashboard en tu celular o computador. Ves: monto acumulado, # transacciones, productos más vendidos, hora de cada venta. Todo actualizado al segundo.' },
            { q: '¿Qué documentación recibo?', a: 'PDF con cuadratura completa (cada transacción con hora, monto, producto), comprobante bancario de transferencia, y boleta/factura por el arriendo del servicio.' },
            { q: '¿Cuánto cuesta el arriendo?', a: 'Desde $290.000 plan Básico (1-2 kioscos, eventos pequeños) hasta plan Pro Masivos para festivales (cotización personalizada). Traslado RM/VI/V sin costo.' },
            { q: '¿Ustedes se quedan con mi dinero?', a: 'No. El dinero va a una cuenta operativa solo para el procesamiento, y se transfiere automáticamente a tu cuenta bancaria. Venddi™ cobra solo la comisión del 2.9% por el servicio de procesamiento.' },
            { q: '¿Qué garantía tengo?', a: 'Respaldo ALLCOM con 20 años en el mercado. Procesamos millones al año sin problemas. Además: transferencia garantizada o eventos futuros gratis.' },
          ].map((faq, i) => (
            <motion.div key={i} variants={fadeUp} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <h5 className="text-sm font-semibold text-[#0A2540] mb-2">{faq.q}</h5>
              <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA Final */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0EA5E9] to-[#0191B4] py-20">
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{fontFamily:'Syne,sans-serif'}}>Tu próximo evento con Venddi™</h2>
          <p className="text-lg text-sky-100 mb-8">+180 eventos operados. Respuesta en 2 horas.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#0EA5E9] rounded-xl hover:shadow-2xl transition-all"><Calendar className="w-5 h-5" /> Agendar</Link>
            <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white border-2 border-white/30 rounded-xl hover:bg-white/10 transition-all"><MessageCircle className="w-5 h-5" /> WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}
